import { __awaiter } from "tslib";
import * as constants from './constants.js';
import { TableWidgets } from './table-widgets.js';
import { Utils } from './utils.js';
import { startCase } from 'lodash-es';
import { waitForChangeSettlement } from './automation/waitForChangeSettlement.js';
import { delay } from './automation/delay.js';
import dompurify from 'dompurify';
import { waitForElement } from './automation/waitForElement.js';
export class Parser {
    constructor(options) {
        this.options = options;
        this.result = [];
        const { maximumTableColumns = 50, featureFlags = {}, waitForSection } = options;
        this.settings = {
            featureFlags,
            maximumTableColumns,
            waitForSection,
        };
        this.tableWidgets = new TableWidgets(this.settings.maximumTableColumns);
    }
    /**
     * Scrapes all the text data as is
     * Schedule of Activities widgets is converted to the table
     * Study Design Schema - PNG image
     * @param query Information needed to scrape the study definition
     */
    studyDefinitionElementsToDocumentHTML(query) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const generalInfo = yield this.generalInformationElementsToDocumentHTML(query);
            this.result = [];
            const headers = [];
            // In the case that the sections are broken into pages we need to navigate the section pages before scraping
            const sectionIds = yield this.studyDefinitionNavElementsToSectionIds(query.studyId);
            let sectionIndex = 0;
            for (const sectionId of sectionIds) {
                yield this.settings.waitForSection(sectionId);
                yield this.studyPageReady(sectionIndex);
                sectionIndex++;
                if (sectionId != null) {
                    const sectionHeaderText = document.querySelector(`[data-parser-id="${sectionId}"]`).textContent;
                    const sectionHeader = document.createElement('h1');
                    sectionHeader.setAttribute('id', sectionId);
                    sectionHeader.textContent = sectionHeaderText;
                    headers.push(sectionHeader);
                    this.result.push(sectionHeader.outerHTML);
                }
                const widgetsSelectors = Object.values(constants.widgets).map(element => `[data-document-parser-id="${element.dataDocumentParserId}"]`);
                const textData = document.querySelectorAll(`${constants.selectorText}, ${widgetsSelectors.join(', ')}`);
                for (const widgetElement of Array.from(textData)) {
                    if (widgetElement.closest(constants.generalInformationSection) ||
                        widgetElement.querySelector(constants.generalInformationSection)) {
                        continue;
                    }
                    // TODO fix O(n*n) here
                    const [widgetName, widgetStructure] = (_a = Object.entries(constants.widgets)
                        .filter(e => widgetElement.getAttribute('data-document-parser-id') === e[1].dataDocumentParserId)
                        .pop()) !== null && _a !== void 0 ? _a : [null, null];
                    if (widgetName && widgetStructure) {
                        if (widgetStructure.header) {
                            this.result.push(widgetStructure.header);
                        }
                        if (widgetStructure.variantsSelector != null) {
                            const contentSelector = widgetStructure.contentSelector;
                            const variantPills = Array.from(document.querySelectorAll(widgetStructure.variantsSelector));
                            // In the case that there are no variants listed as pills we need to take the one and only widget
                            if (variantPills.length < 2) {
                                try {
                                    yield waitForChangeSettlement(contentSelector());
                                    const data = yield this.insertWidget(widgetName, widgetStructure);
                                    this.result.push(data);
                                }
                                catch (timeoutError) {
                                    console.warn(timeoutError);
                                    throw new Error(constants.exportError);
                                }
                            }
                            else {
                                const initialVariantPill = document.querySelector(`${widgetStructure.variantsSelector} [class*=selected]`);
                                for (const variantPill of variantPills) {
                                    const variantId = variantPill.getAttribute('data-document-parser-id');
                                    const variantName = ((_b = variantPill === null || variantPill === void 0 ? void 0 : variantPill.parentElement) === null || _b === void 0 ? void 0 : _b.textContent) || '';
                                    const selectablePillPart = ((variantPill === null || variantPill === void 0 ? void 0 : variantPill.querySelector('[data-selectable="true"]:not(svg)')) || variantPill);
                                    selectablePillPart === null || selectablePillPart === void 0 ? void 0 : selectablePillPart.click();
                                    try {
                                        yield waitForChangeSettlement(contentSelector(variantId));
                                        this.result.push(`<h3>${variantName}</h3>`);
                                        const data = yield this.insertWidget(widgetName, widgetStructure, variantName || '');
                                        this.result.push(data);
                                    }
                                    catch (timeoutError) {
                                        console.warn(timeoutError);
                                        throw new Error(constants.exportError);
                                    }
                                }
                                // Restore initially selected variant
                                initialVariantPill === null || initialVariantPill === void 0 ? void 0 : initialVariantPill.click();
                            }
                        }
                        else {
                            this.result.push(yield this.insertWidget(widgetName, widgetStructure));
                        }
                    }
                    else {
                        // if element isn't a select box or child of another element (<h1><p>)
                        if (widgetElement.getAttribute('role') !== 'listbox' &&
                            !Utils.hasParentFromList(widgetElement, textData)) {
                            const clonedElement = this.formatOrderedList(Utils.cloneNode(widgetElement));
                            Utils.clearTableFromInputs(clonedElement);
                            const tagName = (_c = clonedElement === null || clonedElement === void 0 ? void 0 : clonedElement.firstChild) === null || _c === void 0 ? void 0 : _c.nodeName;
                            if (clonedElement &&
                                (tagName === 'OL' || tagName === 'UL') &&
                                clonedElement.querySelector('table')) {
                                const rebuildList = [];
                                rebuildList.push(`<${tagName}>`);
                                clonedElement.querySelectorAll('li').forEach(e => {
                                    if (!e.querySelector('table')) {
                                        rebuildList.push(e.outerHTML);
                                    }
                                    else {
                                        const tables = [];
                                        e.querySelectorAll('table').forEach(t => {
                                            tables.push(t.outerHTML);
                                            t.outerHTML = '';
                                            rebuildList.push(e.outerHTML);
                                            rebuildList.push(`</${tagName}>${tables.join('')}<${tagName}>`);
                                        });
                                    }
                                });
                                rebuildList.push(`</${tagName}>`);
                                if (clonedElement.firstElementChild) {
                                    clonedElement.firstElementChild.outerHTML = rebuildList.join('');
                                }
                            }
                            clonedElement === null || clonedElement === void 0 ? void 0 : clonedElement.querySelectorAll('button').forEach(b => b.remove());
                            clonedElement === null || clonedElement === void 0 ? void 0 : clonedElement.querySelectorAll('input[type="checkbox"]').forEach(b => {
                                clonedElement === null || clonedElement === void 0 ? void 0 : clonedElement.querySelectorAll('label[for="' + b.id + '"]').forEach(b => b.remove());
                                b.remove();
                            });
                            clonedElement === null || clonedElement === void 0 ? void 0 : clonedElement.querySelectorAll(constants.headers).forEach(h => {
                                var _a;
                                //clear headers from redundant tags
                                h.innerHTML = dompurify.sanitize((_a = h.textContent) !== null && _a !== void 0 ? _a : '');
                                headers.push(h);
                            });
                            const element = Utils.wrapElementIntoDiv(Utils.cleanClasses(this.wrapWithBookmark(clonedElement)));
                            element === null || element === void 0 ? void 0 : element.querySelectorAll('table').forEach(t => {
                                var _a, _b;
                                t.outerHTML = `${(_b = (_a = this.tableWidgets.formatTable(t)) === null || _a === void 0 ? void 0 : _a.outerHTML) !== null && _b !== void 0 ? _b : ''}<br>`;
                            });
                            this.result.push(element.innerHTML);
                        }
                    }
                }
            }
            const tableOfContents = Parser.buildTableOfContents(headers);
            const sections = this.result.join(' ');
            return `${generalInfo} ${tableOfContents} ${sections}`;
        });
    }
    studyDefinitionNavElementsToSectionIds(studyId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.settings.waitForSection();
            const sectionLinkElements = Array.from(document.querySelectorAll(`[data-parser-role="studyDefinitionSectionLink"][data-parser-study-id="${studyId}"]`));
            return sectionLinkElements.map(x => x.getAttribute('data-parser-id'));
        });
    }
    /**
     * Scrapes Study information page and returns it as a string
     */
    generalInformationElementsToDocumentHTML(query) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e;
            // Ensure general information is present before scraping
            yield this.settings.waitForSection();
            yield this.studyPageReady();
            const { revisionName, revisionDate, studyId } = query;
            const sections = document.querySelectorAll(`${constants.generalInformationSection} section`);
            let totalNumberOfInfoRows = 0;
            const groupsOfInfo = [];
            sections.forEach(s => {
                const header = s.querySelector(':scope > h3');
                if (header) {
                    const section = { length: 0, content: '' };
                    section.content = `<h2>${header.textContent}</h2>`;
                    s.querySelectorAll('label[for]').forEach(l => {
                        var _a, _b, _c, _d, _e, _f;
                        const input = document.querySelector(`textarea[id=${(_a = l.htmlFor) !== null && _a !== void 0 ? _a : ''}],input[type=text][id=${(_b = l.htmlFor) !== null && _b !== void 0 ? _b : ''}]`);
                        section.length++;
                        if (input) {
                            section.content += Utils.formInfoRow(startCase((_c = l.textContent) !== null && _c !== void 0 ? _c : ''), (_d = input.value) !== null && _d !== void 0 ? _d : '');
                        }
                        else {
                            const listItem = document.querySelector(`[data-document-parser-id="${l.htmlFor}SelectField"] > div[role="button"],
                            [data-document-parser-id="${l.htmlFor}Field"] > div[role="button"]`);
                            if (listItem) {
                                let textContent = (_e = listItem.textContent) !== null && _e !== void 0 ? _e : '';
                                const selectedItems = listItem.querySelectorAll('div[role="button"]');
                                if (selectedItems.length > 1) {
                                    const multiSelectItems = [];
                                    selectedItems.forEach(i => {
                                        var _a, _b;
                                        if ((_a = i.parentElement) === null || _a === void 0 ? void 0 : _a.textContent)
                                            multiSelectItems.push((_b = i.parentElement) === null || _b === void 0 ? void 0 : _b.textContent);
                                    });
                                    textContent = multiSelectItems.join(', ');
                                }
                                section.content += Utils.formInfoRow(startCase((_f = l.textContent) !== null && _f !== void 0 ? _f : ''), textContent);
                            }
                        }
                    });
                    totalNumberOfInfoRows += section.length;
                    if (section.length)
                        groupsOfInfo.push(section);
                }
            });
            //split info into 2 columns
            let info = '<table class="no-border"><tr><td>';
            let rowsInCell = 0;
            groupsOfInfo.forEach(g => {
                info += g.content;
                rowsInCell += g.length;
                //checks if number of rows into 1st table cell is greater than total number. if yes - starts a new cell
                if (rowsInCell >= totalNumberOfInfoRows / 2) {
                    info += '</td><td>';
                    rowsInCell = 0;
                }
            });
            const shouldDisplayRevisionInformation = [revisionName, revisionDate, studyId].find(x => x != null) != null;
            if (shouldDisplayRevisionInformation) {
                info += `<td> 
                <h2>Revision Information</h2>
                ${studyId != null ? Utils.formInfoRow('Study ID', studyId) : ''}
                ${revisionName != null ? Utils.formInfoRow('Revision Name', revisionName) : ''}
                ${revisionDate != null ? Utils.formInfoRow('Revision Date', revisionDate.toLocaleString()) : ''}
            </td>`;
            }
            info += '</tr></table>';
            //get additional info
            const additionalInfoHeader = (_c = (_b = (_a = document
                .querySelector('[data-document-parser-id="additionalInformation"]')) === null || _a === void 0 ? void 0 : _a.closest('section')) === null || _b === void 0 ? void 0 : _b.querySelector('h3')) === null || _c === void 0 ? void 0 : _c.textContent;
            info += `<h2>${additionalInfoHeader !== null && additionalInfoHeader !== void 0 ? additionalInfoHeader : ''}</h2>`;
            const numberOfRows = document.querySelectorAll('[id*=metada]').length;
            for (let i = 0; i < numberOfRows / 2; i++) {
                const label = (_d = document.querySelector(`[id="metadata.${i}.0"]`)) === null || _d === void 0 ? void 0 : _d.value;
                const value = (_e = document.querySelector(`[id="metadata.${i}.1"]`)) === null || _e === void 0 ? void 0 : _e.value;
                if (label || value) {
                    info += Utils.formInfoRow(label !== null && label !== void 0 ? label : '', value !== null && value !== void 0 ? value : '');
                }
            }
            return info;
        });
    }
    /**
     * waits until all the dynamic elements are loaded
     * @private
     */
    studyPageReady(sectionIndex) {
        return __awaiter(this, void 0, void 0, function* () {
            // wait 1sec while all animations are finished
            yield delay(constants.beforeScrapingTimeout);
            // wait until all reference values are loaded
            if (document.querySelector(constants.objectiveReferenceSpinner) != null) {
                yield this.elementReady(document.body, null, '', () => {
                    return !document.querySelector(constants.objectiveReferenceSpinner);
                });
            }
            if (document.querySelector(constants.referenceValueSpinner)) {
                yield this.elementReady(document.body, null, '', () => {
                    return !document.querySelector(constants.referenceValueSpinner);
                });
            }
            if (sectionIndex) {
                switch (sectionIndex) {
                    case 4:
                        try {
                            yield waitForElement(`[data-document-parser-id="${constants.widgets.scheduleOfActivities.dataDocumentParserId}"]`);
                        }
                        catch (timeoutError) {
                            console.warn(timeoutError);
                            throw new Error(constants.exportError);
                        }
                        break;
                    case 3:
                        try {
                            yield waitForElement(`[data-document-parser-id="${constants.widgets.studyDesignSchema.dataDocumentParserId}"]`);
                        }
                        catch (timeoutError) {
                            console.warn(timeoutError);
                            throw new Error(constants.exportError);
                        }
                        break;
                }
            }
        });
    }
    /**
     * there is a bug with numeration of ordered lists <ol>
     * need to add type attribute manually
     * @param textBlock
     * @private
     */
    formatOrderedList(textBlock) {
        if (textBlock) {
            textBlock.querySelectorAll('ol').forEach(e => {
                const countParents = Utils.countAllParentsBySelector(e, 'ol');
                e.type =
                    countParents < constants.orderedListTypes.length
                        ? constants.orderedListTypes[countParents]
                        : constants.orderedListTypes[constants.orderedListTypes.length - 1];
            });
        }
        return textBlock;
    }
    /**
     * use MutationObserver to check if element is rendered
     * @param element
     * @param emitter
     * @param widgetName
     * @param observeFunction
     * @private
     */
    elementReady(element, emitter, widgetName, observeFunction) {
        return Utils.promiseTimeout(constants.mutationObserveTimeout, widgetName, new Promise(resolve => {
            var _a;
            new MutationObserver((mutationList, observer) => {
                if (observeFunction(mutationList)) {
                    resolve(true);
                    observer.disconnect();
                }
            }).observe(element, constants.mutationObserveConfig);
            if (emitter) {
                (_a = emitter.querySelector('div')) === null || _a === void 0 ? void 0 : _a.click();
            }
        }));
    }
    /**
     * adding table widget or image to the result
     * @returns {string}
     * @param {string} widgetName
     * @param {widgetProperties} widgetStructure
     * @param {string} stageName
     * @private
     */
    insertWidget(widgetName_1, widgetStructure_1) {
        return __awaiter(this, arguments, void 0, function* (widgetName, widgetStructure, stageName = '') {
            return widgetStructure.isImageOnly
                ? yield Parser.insertImage(`[data-document-parser-id="${widgetStructure.dataDocumentParserId}"]>div>div`)
                : yield this.insertTableWidget(widgetName, stageName);
        });
    }
    /**
     * @returns {boolean} - image tag was inserted or not
     * @param {string} selector - any HTML selector
     * @private
     */
    static insertImage(selector) {
        return __awaiter(this, void 0, void 0, function* () {
            const image = yield Utils.makeScreenshot(selector);
            return `<p style="text-align: center"><img src="${image}" alt="" /></p>`;
        });
    }
    /**
     *
     * scrapes all the table represented widgets
     * @param {string} widgetName - name of the scraped widget, {@link widgets} definition
     * @param {string} stageName - name of the stage for adaptive studies
     * @private
     */
    insertTableWidget(widgetName, stageName) {
        return __awaiter(this, void 0, void 0, function* () {
            const tableWidgetsService = new TableWidgets(this.settings.maximumTableColumns);
            switch (widgetName) {
                case 'scheduleOfActivities':
                    return tableWidgetsService.getScheduleOfActivity(stageName);
                case 'interventionGroups':
                    return tableWidgetsService.getStudyInterventionGroups();
                default:
                    return tableWidgetsService.getTableWidget(widgetName);
            }
        });
    }
    /**
     * @returns {string}
     * @param {Element[]} headers
     * @private
     */
    static buildTableOfContents(headers) {
        let tableOfContents = '<h2>Table of Content</h2>';
        let previousTagName = 0;
        headers.forEach(h => {
            const current = parseInt(h.tagName[1], constants.numberRadix);
            if (current <= 3) {
                const link = `<li><a class="link" href="#${h.id}">${h.textContent}</a></li>`;
                if (!previousTagName || previousTagName < current) {
                    tableOfContents += `<ol class="table-of-content">${link}`;
                }
                else if (previousTagName > current) {
                    tableOfContents += `${'</ol>'.repeat(previousTagName - current)}${link}`;
                }
                else {
                    tableOfContents += link;
                }
                previousTagName = parseInt(h.tagName[1], constants.numberRadix);
            }
        });
        return tableOfContents + '</ol>';
    }
    /**
     * For Google Docs bookmarks work only for <a> element with "name" attribute
     * @param {Element | null } element
     * @private
     */
    wrapWithBookmark(element) {
        if (element) {
            const wrapper = Utils.wrapElementIntoDiv(element.outerHTML);
            wrapper.querySelectorAll('[id]').forEach(e => {
                var _a;
                if (e.getAttribute('contenteditable') !== 'true') {
                    e.innerHTML = `<a class="bookmark" name="${(_a = e.id) !== null && _a !== void 0 ? _a : ''}"></a>${e.innerHTML}`;
                    e.removeAttribute('id');
                }
            });
            return wrapper.innerHTML;
        }
        return '';
    }
}
//# sourceMappingURL=parser.js.map